import { ChevronDown, Eye } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { PIPELINE_STAGE_LABELS, type TemplateAudience } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SearchInput } from '../../../../components/Input';
import { MenuGroup, MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { Segmented } from '../../../../components/Segmented';
import { SkeletonLines } from '../../../../components/Skeleton';
import { errorMessage } from '../../../../lib/api-client';
import { EMPTY } from '../../../../lib/format';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { useSettledValue } from '../../../../lib/use-settled-value';
import { usePreviewPeople, useTemplatePreview } from '../../api/communication-api';
import styles from './TemplatePreviewPane.module.css';

/** The application the preview fills from, with the words its button shows. */
export interface PreviewChoice {
  readonly applicationId: string;
  readonly label: string;
}

export type PreviewDevice = 'desktop' | 'phone';

/** The preview draws once typing pauses this long (06-qol §6's 250 ms, and a little for the email to render). */
const SETTLE_MS = 300;

/**
 * ADMIN-09's right pane (Option A): the email exactly as it would arrive, from the words being written. "As …" picks a
 * real application (a search beside its button); without one, and for what no application holds, the values are
 * labelled samples. Desktop and phone set the frame's width. The email is drawn in a sandboxed frame that runs no
 * script and opens nothing; the server never sends or links anything for it.
 */
export function TemplatePreviewPane({
  templateId,
  audience,
  subject,
  bodyText,
  choice,
  device,
  onChoice,
  onDevice,
}: {
  readonly templateId: string | null;
  readonly audience: TemplateAudience;
  /** Already turned back into variables (`fromLabelledText`). */
  readonly subject: string;
  readonly bodyText: string;
  readonly choice: PreviewChoice | null;
  readonly device: PreviewDevice;
  readonly onChoice: (choice: PreviewChoice | null) => void;
  readonly onDevice: (device: PreviewDevice) => void;
}): ReactNode {
  const isForCandidate = audience === 'candidates';
  const input = useSettledValue({ templateId, subject, bodyText, applicationId: isForCandidate ? (choice?.applicationId ?? null) : null }, SETTLE_MS);
  const preview = useTemplatePreview(input, true);
  const shown = preview.data;
  const hasSamples = shown?.values.some((value) => value.isSample) ?? false;

  return (
    <section className={styles.pane} aria-label="Preview">
      <div className={styles.head}>
        <h2 className={styles.title}>
          <Eye size={16} aria-hidden /> Preview
        </h2>
        <span className={styles.grow} />
        {isForCandidate && <PreviewAs choice={choice} onChoice={onChoice} />}
        <Segmented
          label="Preview width"
          value={device}
          options={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'phone', label: 'Phone' },
          ]}
          onChange={onDevice}
        />
      </div>

      {preview.isError && (
        <div className={styles.problem} role="alert">
          <span>{errorMessage(preview.error)}</span>
          {choice && (
            <Button variant="quiet" size="sm" onClick={() => onChoice(null)}>
              Use sample values
            </Button>
          )}
        </div>
      )}

      {!shown ? (
        <div className={styles.frame}>
          <SkeletonLines count={8} />
        </div>
      ) : (
        <div className={styles.frame} data-device={device}>
          <dl className={styles.envelope}>
            <div>
              <dt>From</dt>
              <dd>{shown.from}</dd>
            </div>
            <div>
              <dt>To</dt>
              <dd>{shown.candidate ? `${shown.candidate.fullName} <${shown.candidate.restrictedFields?.includes('email') || !shown.candidate.email ? EMPTY : shown.candidate.email}>` : (shown.sampleRecipient ?? EMPTY)}</dd>
            </div>
            <div className={styles.subjectLine}>
              <dt className={styles.hidden}>Subject</dt>
              <dd>{shown.subject.trim() === '' ? EMPTY : shown.subject}</dd>
            </div>
          </dl>
          {/* No script, no forms, no pop-ups, nothing of ours reachable from inside (sandbox=""). */}
          <iframe className={styles.email} title="The email as it would arrive" sandbox="" srcDoc={shown.html} data-pending={preview.isFetching || undefined} />
        </div>
      )}

      {shown && shown.unknownVariables.length > 0 && <p className={styles.warn}>This email can’t fill {shown.unknownVariables.join(', ')}. They show as written, marked.</p>}
      {shown && hasSamples && (
        <p className={styles.note}>
          Highlighted words are samples{isForCandidate ? '' : ': an email to staff has no application to fill it'}. The real email fills them in when it is sent, and its link is made then.
        </p>
      )}
    </section>
  );
}

/** "As Mithila Sarker · Junior merchandiser": a search beside the button, by name, email or mobile. */
function PreviewAs({ choice, onChoice }: { readonly choice: PreviewChoice | null; readonly onChoice: (choice: PreviewChoice | null) => void }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const settled = useSettledValue(query, 250);
  const people = usePreviewPeople(settled, isOpen);
  useFocusOnOpen(searchRef, isOpen);

  const pick = (next: PreviewChoice | null): void => {
    onChoice(next);
    close();
  };

  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" aria-expanded={isOpen} onClick={toggle}>
        <span className={styles.asLabel}>{choice ? `As ${choice.label}` : 'As a sample person'}</span>
        <ChevronDown size={14} aria-hidden />
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={360} label="Preview as">
        <div className={styles.picker}>
          <SearchInput ref={searchRef} aria-label="Find an application" placeholder="Name, email or mobile" value={query} onChange={(event) => setQuery(event.target.value)} />
          <div className={styles.results}>
            {choice && <MenuItem onClick={() => pick(null)}>Use sample values</MenuItem>}
            {settled.trim().length < 2 ? (
              <p className={styles.hint}>Type a name, email or mobile to preview as someone who applied.</p>
            ) : people.data && people.data.length === 0 ? (
              <p className={styles.hint}>No one found. Check the spelling, or try their mobile number.</p>
            ) : (
              (people.data ?? []).map((person) => (
                <MenuGroup key={person.candidate.id} label={person.candidate.fullName}>
                  {person.applications.length === 0 ? (
                    <p className={styles.hint}>No application you can preview as.</p>
                  ) : (
                    person.applications.map((application) => (
                      <MenuItem
                        key={application.id}
                        isSelected={choice?.applicationId === application.id}
                        meta={`${application.ventureName} · ${PIPELINE_STAGE_LABELS[application.stage]}${application.isOpen ? '' : ' · closed'}`}
                        onClick={() => pick({ applicationId: application.id, label: `${person.candidate.fullName} · ${application.jobTitle}` })}
                      >
                        {application.jobTitle}
                      </MenuItem>
                    ))
                  )}
                </MenuGroup>
              ))
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}
